/**
 * Rooms State Store
 * Manages MUC room state using Zustand
 */
import { create } from 'zustand';
// ============================================================================
// Store
// ============================================================================
export const useRoomsStore = create((set, get) => ({
    // Initial state
    backend: null,
    rooms: new Map(),
    // Actions
    setBackend: (backend) => {
        // Register room occupant update handler
        backend.setEventHandlers({
            onRoomOccupantUpdate: (roomJid, occupants) => {
                get().updateOccupants(roomJid, occupants);
            },
        });
        set({ backend });
    },
    loadMyRooms: async () => {
        const { backend, rooms } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        try {
            const myRooms = await backend.getMyRooms();
            // Add all assigned rooms to state (not joined, just discovered)
            for (const xmppRoom of myRooms) {
                rooms.set(xmppRoom.jid, {
                    info: xmppRoom,
                    occupants: [],
                    joined: false,
                    loading: false,
                });
            }
            set({ rooms: new Map(rooms) });
        }
        catch (error) {
            console.error('[RoomsStore] Load my rooms failed:', error);
            throw error;
        }
    },
    joinRoom: async (roomJid, nickname) => {
        const { backend, rooms } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        // Set loading state
        const existingRoom = rooms.get(roomJid);
        if (existingRoom) {
            rooms.set(roomJid, { ...existingRoom, loading: true });
            set({ rooms: new Map(rooms) });
        }
        try {
            await backend.joinRoom(roomJid, nickname);
            // Load room info and wrap in XMPPRoom structure
            const discoInfo = await backend.getRoomInfo(roomJid);
            const occupants = await backend.getRoomOccupants(roomJid);
            rooms.set(roomJid, {
                info: { jid: roomJid, info: discoInfo },
                occupants,
                joined: true,
                nickname,
                loading: false,
            });
            set({ rooms: new Map(rooms) });
        }
        catch (error) {
            console.error('[RoomsStore] Join room failed:', error);
            // Clear loading state
            if (existingRoom) {
                rooms.set(roomJid, { ...existingRoom, loading: false });
                set({ rooms: new Map(rooms) });
            }
            throw error;
        }
    },
    leaveRoom: async (roomJid) => {
        const { backend, rooms } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        try {
            await backend.leaveRoom(roomJid);
            const room = rooms.get(roomJid);
            if (room) {
                const updatedRoom = {
                    ...room,
                    joined: false,
                };
                delete updatedRoom.nickname;
                rooms.set(roomJid, updatedRoom);
                set({ rooms: new Map(rooms) });
            }
        }
        catch (error) {
            console.error('[RoomsStore] Leave room failed:', error);
            throw error;
        }
    },
    loadRoomInfo: async (roomJid) => {
        const { backend, rooms } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        const existingRoom = rooms.get(roomJid);
        if (existingRoom) {
            rooms.set(roomJid, { ...existingRoom, loading: true });
            set({ rooms: new Map(rooms) });
        }
        try {
            const discoInfo = await backend.getRoomInfo(roomJid);
            const occupants = await backend.getRoomOccupants(roomJid);
            const room = rooms.get(roomJid);
            const newRoom = {
                info: { jid: roomJid, info: discoInfo },
                occupants,
                joined: room?.joined || false,
                loading: false,
            };
            if (room?.nickname !== undefined) {
                newRoom.nickname = room.nickname;
            }
            rooms.set(roomJid, newRoom);
            set({ rooms: new Map(rooms) });
        }
        catch (error) {
            console.error('[RoomsStore] Load room info failed:', error);
            if (existingRoom) {
                rooms.set(roomJid, { ...existingRoom, loading: false });
                set({ rooms: new Map(rooms) });
            }
            throw error;
        }
    },
    updateOccupants: (roomJid, occupants) => {
        const { rooms } = get();
        const room = rooms.get(roomJid);
        if (room) {
            rooms.set(roomJid, {
                ...room,
                occupants,
            });
            set({ rooms: new Map(rooms) });
        }
    },
    sendMessage: async (roomJid, body) => {
        const { backend } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        try {
            return await backend.sendGroupchatMessage(roomJid, body);
        }
        catch (error) {
            console.error('[RoomsStore] Send message failed:', error);
            throw error;
        }
    },
    setRoomSubject: async (roomJid, subject) => {
        const { backend } = get();
        if (!backend) {
            throw new Error('Backend not initialized');
        }
        try {
            await backend.setRoomSubject(roomJid, subject);
        }
        catch (error) {
            console.error('[RoomsStore] Set subject failed:', error);
            throw error;
        }
    },
    // Queries
    getRoom: (roomJid) => {
        return get().rooms.get(roomJid);
    },
    getJoinedRooms: () => {
        const { rooms } = get();
        return Array.from(rooms.values()).filter((room) => room.joined);
    },
}));
// ============================================================================
// Selectors
// ============================================================================
export const selectRoom = (roomJid) => (state) => state.rooms.get(roomJid);
export const selectRoomOccupants = (roomJid) => (state) => state.rooms.get(roomJid)?.occupants || [];
export const selectIsJoined = (roomJid) => (state) => state.rooms.get(roomJid)?.joined || false;
export const selectJoinedRooms = (state) => Array.from(state.rooms.values()).filter((room) => room.joined);
export const selectAllRooms = (state) => Array.from(state.rooms.values());
//# sourceMappingURL=rooms.js.map